import { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Calendar, 
  CalendarDays, 
  Mail, 
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Plus,
  Edit,
  Users,
  User,
  LayoutDashboard,
  Upload,
  Image as ImageIcon,
  Images,
  Ticket,
  Building,
  Settings,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminMessages({ credentials, userRole }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  const canModify = userRole === 'admin';

  const headers = useMemo(() => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  }, [credentials.username, credentials.password]);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contact`, { headers });
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${API}/contact/${id}`, {}, { headers });
      fetchMessages();
    } catch (error) {
      toast.error("Failed to update message");
    }
  };

  const deleteMessage = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/contact/${deleteId}`, { headers });
      toast.success("Message deleted");
      setDeleteId(null);
      fetchMessages();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  return (
    <div data-testid="admin-messages">
      <h1 className="heading-lg mb-8">Messages</h1>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : messages.length === 0 ? (
        <Card className="card-heritage">
          <CardContent className="p-12 text-center">
            <Mail className="w-12 h-12 text-[#D6D3D1] mx-auto mb-4" />
            <p className="text-[#78716C]">No messages yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => (
            <Card 
              key={message.id} 
              className={`card-heritage ${!message.is_read ? 'border-l-4 border-l-[#D97706]' : ''}`}
              data-testid={`message-${message.id}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-[#1C1917]">{message.subject}</h3>
                      {!message.is_read && (
                        <span className="px-2 py-0.5 bg-[#D97706] text-white text-xs rounded">New</span>
                      )}
                    </div>
                    <p className="text-sm text-[#78716C] mb-1">From: {message.name} ({message.email})</p>
                    <p className="text-sm text-[#78716C] line-clamp-1">{message.message}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setSelectedMessage(message);
                            if (!message.is_read) markAsRead(message.id);
                          }}
                        >
                          <Eye size={16} className="mr-1" /> View
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="font-serif">{selectedMessage?.subject}</DialogTitle>
                          <DialogDescription>
                            From: {selectedMessage?.name} ({selectedMessage?.email})
                            {selectedMessage?.phone && ` | ${selectedMessage.phone}`}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="mt-4">
                          <p className="whitespace-pre-wrap">{selectedMessage?.message}</p>
                        </div>
                        <DialogFooter className="mt-4">
                          <a href={`mailto:${selectedMessage?.email}`}>
                            <Button className="btn-primary">
                              <Mail size={16} className="mr-2" /> Reply
                            </Button>
                          </a>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-[#B91C1C]"
                      onClick={() => setDeleteId(message.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Message?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the message.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteMessage} className="bg-[#B91C1C] hover:bg-[#991B1B]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Gallery Component
